import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "@mui/styles";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { UsersManagementProps } from "../../../../pages/v2/UsersPage";
import { ViewLtiAssignmentMetadata } from "@backend-types";
import { formatDateMst, formatTimeMst } from "../../../utils/MSTDateTimeFormatter";
// import { Button, Dialog, Stack, Typography } from "@mui/material";

type AssignmentRow = ViewLtiAssignmentMetadata & {
  action?: never;
};

interface ColumnWidths {
  [field: string]: number;
}

const useStyles = makeStyles((theme: any) => ({
  root: { textAlign: "left", display: "flex", flexDirection: "column" },
  dataTable: {
    borderRadius: "8px",
    backgroundColor: "white",
    borderColor: "#E8E8E8",
    "& .MuiDataGrid-iconButtonContainer": { marginLeft: "auto" },
    "& .MuiDataGrid-columnHeaders": { height: "50px", minHeight: "unset" },
    "& .MuiDataGrid-columnHeader": { "&:focus, &:focus-within": { outline: "none" } },
    "& .MuiDataGrid-columnHeader--sorted": {
      color: theme.palette.primary.main,
      "& .MuiDataGrid-sortIcon": { color: theme.palette.primary.main },
    },
    "& .MuiDataGrid-cell": {
      border: "0px",
      "&:focus, &:focus-within": { outline: "none" },
    },
  },
  dataTableHeader: { "& > .MuiDataGrid-columnSeparator": { visibility: "hidden" } },
  passbackDialog: {
    padding: 32,
    maxWidth: 511,
    height: 181,
    position: "relative",
    border: `2px solid ${theme.palette.divider}`,
    borderRadius: 15,
  },
  buttonPassbackDialog: { width: 139, height: 50, padding: 0, fontSize: "19px" },
}));

export const LtiAssignmentsTable: React.FC<UsersManagementProps> = (props) => {
  const classes = useStyles();

  // const [showEditDialog, setShowEditDialog] = useState(false);
  // const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const [columnWidths, setColumnWidths] = useState<ColumnWidths>({});
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);

  const rows = (props.assignments || []) as AssignmentRow[];

  // Refs to avoid dependency churn
  const rowsRef = useRef<AssignmentRow[]>(rows);
  rowsRef.current = rows;

  const baseColumnsRef = useRef<GridColDef<AssignmentRow>[]>([]);
  const columnWidthsRef = useRef<ColumnWidths>({});
  useEffect(() => {
    columnWidthsRef.current = columnWidths;
  }, [columnWidths]);

  const displayTextWidth = useCallback((text: string, font: string, fontSize: string) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return 0;
    ctx.font = `${fontSize} ${font}`;
    return ctx.measureText(text).width + 50;
  }, []);

  const setColumnWidthsIfChanged = useCallback((next: ColumnWidths) => {
    const prev = columnWidthsRef.current;

    const prevKeys = Object.keys(prev);
    const nextKeys = Object.keys(next);
    if (prevKeys.length !== nextKeys.length) {
      setColumnWidths(next);
      return;
    }

    for (const k of nextKeys) {
      if (prev[k] !== next[k]) {
        setColumnWidths(next);
        return;
      }
    }
    // unchanged: do nothing
  }, []);

  const calculateWidths = useCallback(
    (nextPage: number, nextPageSize: number) => {
      const rowsNow = rowsRef.current;
      const colsNow = baseColumnsRef.current;

      const slice = rowsNow.slice(
        nextPage * nextPageSize,
        nextPage * nextPageSize + nextPageSize
      );

      const newWidths: ColumnWidths = {};

      for (const col of colsNow) {
        const field = col.field;
        if (!field || field === "action") continue;
        if (col.hide) continue;

        let longest = col.headerName ?? field;

        for (const r of slice) {
          const v = (r as any)[field];
          const s =
            v === null || v === undefined
              ? ""
              : typeof v === "boolean"
              ? v
                ? "true"
                : "false"
              : String(v);

          if (s.length > longest.length) longest = s;
        }

        const headerWidth = displayTextWidth(col.headerName ?? field, "Roboto", "14px");
        const valueWidth = displayTextWidth(longest, "Roboto", "14px");
        const measured = Math.max(headerWidth, valueWidth);

        newWidths[field] = Math.min(Math.max(measured, 120), 520);
      }

      setColumnWidthsIfChanged(newWidths);
    },
    [displayTextWidth, setColumnWidthsIfChanged]
  );

  // Build base columns ONCE per style-class change (no columnWidths dependency!)
  const baseColumns = useMemo<GridColDef<AssignmentRow>[]>(() => {
    const renderCell = (params: any) => (
      <div
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          width: "100%",
        }}>
        {params.value ?? ""}
      </div>
    );

    const renderBool = (params: any) => (
      <div
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          width: "100%",
        }}>
        {params.value === true ? "true" : params.value === false ? "false" : ""}
      </div>
    );

    const renderDate = (params: any) => (
      <div
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          width: "100%",
        }}>
        {params.value
          ? `${formatDateMst(params.value)} ${formatTimeMst(params.value)} MST`
          : ""}
      </div>
    );

    const c = (
      field: keyof AssignmentRow,
      headerName: string,
      flex = 1
    ): GridColDef<AssignmentRow> => ({
      field: field as string,
      headerName,
      flex,
      headerClassName: classes.dataTableHeader,
      minWidth: 100,
      renderCell,
    });

    const b = (
      field: keyof AssignmentRow,
      headerName: string,
      flex = 1
    ): GridColDef<AssignmentRow> => ({
      field: field as string,
      headerName,
      flex,
      headerClassName: classes.dataTableHeader,
      minWidth: 100,
      renderCell: renderBool,
    });

    const d = (
      field: keyof AssignmentRow,
      headerName: string,
      flex = 1
    ): GridColDef<AssignmentRow> => ({
      field: field as string,
      headerName,
      flex,
      headerClassName: classes.dataTableHeader,
      minWidth: 100,
      renderCell: renderDate,
    });

    const cols = [
      // c("lti_assignment_id", "LTI Assignment ID", 1),
      c("class_id", "Class ID", 1),
      c("resource_id", "Resource ID", 1.5),
      c("deeplink_asset_type", "Deeplink Asset Type", 1.2),
      d("canvas_assignment_due_date", "Canvas Due Date", 1.2),
      c("canvas_assignment_name", "Canvas Assignment Name", 1.8),

      // c("course_id", "Course ID", 1),
      // c("course_name", "Course Name", 1.5),
      c("class_subject", "Class Subject", 1),
      c("section_sln", "Section SLN", 1),
      c("canvas_section_number", "Canvas Section #", 1.2),

      c("grading_method", "Grading Method", 1.2),
      c("grading_trigger", "Grading Trigger", 1.2),
      c("late_penalty", "Late Penalty", 1),
      c("max_days_late", "Max Days Late", 1),
      // c("canvas_assignment_id", "Canvas Assignment ID", 1.2),

      b("is_synced", "Is Synced", 0.8),

      c("canvas_lp_id", "Canvas LP ID", 1.2),
      c("canvas_lp_interval", "Canvas LP Interval", 1.2),
      c("canvas_lp_late_deduction", "LP Late Deduction", 1.1),
      c("canvas_lp_missing_deduction", "LP Missing Deduction", 1.2),
      c("canvas_lp_min_percent", "LP Min %", 0.9),
      b("canvas_lp_late_enabled", "LP Late Enabled", 1),
      b("canvas_lp_missing_enabled", "LP Missing Enabled", 1.1),
      b("canvas_lp_min_percent_enabled", "LP Min % Enabled", 1.2),

      c("canvas_term_name", "Canvas Term Name", 1.3),

      // c("lti_client_id", "LTI Client ID", 1.2),
      // c("lti_tool_client_id", "LTI Tool Client ID", 1.4),
      c("lti_daily_late_penalty", "LTI Daily Late Penalty", 1.3),
      c("lti_block_past_due_days", "LTI Block Past Due Days", 1.3),
      b("block_past_due_grade_passback", "Block Past Due Passback", 1.4),
      c("lti_grading_method", "LTI Grading Method", 1.2),
      c("lti_grading_trigger", "LTI Grading Trigger", 1.2),
      // c("faq_course_tag", "FAQ Course Tag", 1.1),
      // c("feedback_survey_link", "Feedback Survey Link", 1.8),
    ] as GridColDef<AssignmentRow>[];

    return cols;
  }, [classes.dataTableHeader]);

  // Keep ref updated so calculateWidths can read without deps
  useEffect(() => {
    baseColumnsRef.current = baseColumns;
  }, [baseColumns]);

  // Final columns = base columns + measured widths
  const columns = useMemo(() => {
    return baseColumns.map((col) => {
      const w = col.field ? columnWidths[col.field] : undefined;
      if (!w) return col;

      return {
        ...col,
        width: w,
        minWidth: w,
      };
    });
  }, [baseColumns, columnWidths]);

  // Recalc widths when data changes or pagination changes
  useEffect(() => {
    if (rows.length === 0) return;

    const maxPage = Math.max(0, Math.ceil(rows.length / pageSize) - 1);
    const safePage = Math.min(page, maxPage);

    // If we need to correct page, do it, but DO NOT calculate widths twice.
    if (safePage !== page) {
      setPage(safePage);
      return;
    }

    calculateWidths(safePage, pageSize);
  }, [rows.length, page, pageSize, calculateWidths]);

  return (
    <div className={classes.root}>
      <div style={{ width: "100%" }}>
        <DataGrid
          className={classes.dataTable}
          rows={rows}
          columns={columns}
          getRowId={(row) => row.lti_assignment_id}
          autoHeight
          disableSelectionOnClick
          disableColumnSelector
          disableDensitySelector
          disableColumnMenu
          hideFooter={rows.length > pageSize ? undefined : true}
          pagination={rows.length > pageSize ? true : undefined}
          pageSize={pageSize}
          page={page}
          onPageChange={(nextPage) => {
            setPage(nextPage);
            calculateWidths(nextPage, pageSize);
          }}
          onPageSizeChange={(nextPageSize) => {
            setPageSize(nextPageSize);
            setPage(0);
            calculateWidths(0, nextPageSize);
          }}
        />
        {/* <Dialog
          fullWidth
          classes={{
            paper: classes.passbackDialog,
          }}
          aria-labelledby="passback-failed-dialog"
          open={showEditDialog}>
          <Stack
            direction="column"
            justifyContent="space-between"
            sx={{ height: "100%" }}>
            <Typography variant="body1" fontWeight={400} lineHeight="18.75px">
              Edit assignment due date
            </Typography>
            <Stack direction="row" justifyContent="center" gap="16px" marginTop="auto">
              <Button
                onClick={() => {
                  setShowEditDialog(false);
                }}
                variant="outlined"
                color="primary"
                size="large"
                className={classes.buttonPassbackDialog}>
                Close
              </Button>
              <Button
                onClick={handleSaveChanges}
                variant="contained"
                color="primary"
                size="large"
                className={classes.buttonPassbackDialog}>
                Save
              </Button>
            </Stack>
          </Stack>
        </Dialog> */}
        {/* <Dialog
          fullWidth
          classes={{
            paper: classes.passbackDialog,
          }}
          aria-labelledby="passback-failed-dialog"
          open={showDeleteDialog}>
          <Stack
            direction="column"
            justifyContent="space-between"
            sx={{ height: "100%" }}>
            <Typography variant="body1" fontWeight={400} lineHeight="18.75px">
              Delete this assignment?
            </Typography>
            <Stack direction="row" justifyContent="center" gap="16px" marginTop="auto">
              <Button
                onClick={() => {
                  setShowDeleteDialog(false);
                }}
                variant="outlined"
                color="primary"
                size="large"
                className={classes.buttonPassbackDialog}>
                Close
              </Button>
              <Button
                onClick={handleDeleteAssignment}
                variant="contained"
                color="primary"
                size="large"
                className={classes.buttonPassbackDialog}>
                Delete
              </Button>
            </Stack>
          </Stack>
        </Dialog> */}
      </div>
    </div>
  );
};
